// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the stream token this browser sends to the stream host (security
 * fix plan 2026-10, S-02, step 3). The site's `GET /api/stream/token`
 * signs a ten-minute token from the account's plan, or from what a meeting
 * lends this seat (`?room=&ticket=`); it goes on every request to the
 * stream host's gated files as `Authorization: Bearer <token>`, never in an
 * address, through `streamAuthFetch` and the dash.js interceptors here.
 *
 * What stays plain: `index.json`, thumbnails, colorbars, the shared
 * borders picture and the AR marker (the host's public allow-list,
 * DECISION 1), and every other origin. Own data keeps its own tokens
 * (`privateData.ts`): those scopes are looked at first.
 *
 * One token per page: fetched once the account is known (or a meeting
 * joined), renewed at three quarters of its life, fetched again after a
 * 401 from the host (at most once a minute), dropped on sign-out or on
 * leaving the meeting. A refusal (signed out, a plan with no streams, the
 * route not configured) is remembered for a minute so a page full of
 * stream requests asks once, not once per segment. The stream host is
 * still open today: without a token the requests go out as before.
 */

import { accountState, onAccountChange } from './account'
import { authHeaderFor, dashAuthInterceptor, renewalDelay, RENEW_RETRY_MS } from './privateData'
import { logger } from '../utils/logger'
import { streamHostBase } from './streamHost'

/** What the site answered: the token and the host it is for (`base`, a root URL ending in `/`). */
export interface StreamToken {
  token: string
  /** Issued at, epoch ms. */
  issuedAt: number
  /** Ends at, epoch ms. */
  expiresAt: number
  base: string
}

/** Why the host, or the site, refused: for the notice the app shows. */
export type StreamDenial = 'signed_out' | 'no_streams' | 'unauthorized'

/**
 * A stream the host refused (401): the notice has been shown through
 * `onStreamDenied`, so a load that fails with this is not a load error
 * to show as well.
 */
export class StreamRefusedError extends Error {
  readonly status = 401
  constructor(url: string) {
    super(`The stream host refused ${url} (401)`)
    this.name = 'StreamRefusedError'
  }
}

/** The route that signs tokens. */
export const STREAM_TOKEN_URL = '/api/stream/token'
/** After a refusal or a failure, do not ask the site again sooner than this. */
export const ASK_AGAIN_AFTER_MS = 60_000
/** A 401 from the host gets the token fetched again at most this often. */
export const UNAUTHORIZED_REFETCH_MS = 60_000
/** The AR marker's key on the stream host (`src/ui/anchorPanel.ts` `ANCHOR_MARKER_KEY`); public. */
const ANCHOR_MARKER_PATH = '/shared/xr/ayni-xr-anchor-v1.svg'
/**
 * Files the host serves to everyone (fix plan DECISION 1): by suffix and by prefix.
 * Since 2026-11-01 also the two feeds every visitor sees, signed out included:
 * the default Earth's clouds (cloudFrame.ts) and the Satellites layer's
 * CelesTrak mirror (satellites/satelliteFeed.ts). The gateway keeps the same
 * list (AYNI-2D workers/package-gateway/src/public-surface.ts PUBLIC_CONTENT).
 */
const PUBLIC_SUFFIXES = ['thumbnail_small.png', 'thumbnail.png', '_colorbar.png', 'colorbar.png']
const PUBLIC_PREFIXES = ['/global/projection/shared/', '/global/realtime/noaa-sos/clouds/', '/global/realtime/celestrak/']

let held: StreamToken | null = null
let inFlight: Promise<string | null> | null = null
/** Epoch ms before which the site is not asked again. */
let askAgainAt = 0
let lastUnauthorizedAt = 0
let lastDenial: StreamDenial | null = null
let meeting: { room: string; ticket: string } | null = null
/** A Studio render (`?render=1&job=&t=`): the token is asked for the job, with its account's plan. */
let renderJob: { job: string; t: string } | null = null
let renewTimer: ReturnType<typeof setTimeout> | null = null
const deniedListeners = new Set<(why: StreamDenial) => void>()
let lastToldAt = 0

/** The stream host's origin: the build setting, or the stream host itself when none is set (`streamHost.ts`); null only for an unparsable setting. */
export function configuredStreamOrigin(): string | null {
  try {
    return new URL(streamHostBase()).origin
  } catch {
    return null
  }
}

/** The origin the token is sent to: the token's own `base` once held, else the configured host. */
export function streamOrigin(): string | null {
  if (held) {
    try {
      return new URL(held.base).origin
    } catch {
      return null
    }
  }
  return configuredStreamOrigin()
}

/** Whether a path on the stream host is one the host serves without a token. */
export function isPublicStreamPath(pathname: string): boolean {
  if (pathname === '/index.json' || pathname === ANCHOR_MARKER_PATH) return true
  return PUBLIC_SUFFIXES.some((s) => pathname.endsWith(s)) || PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))
}

/** Whether a request to `url` carries the stream token: on the stream origin, not a public file, no credentials in the address. */
export function needsStreamToken(url: string, origin: string | null = streamOrigin(), baseHref?: string): boolean {
  if (!origin) return false
  let u: URL
  try {
    u = new URL(url, baseHref ?? (typeof location !== 'undefined' ? location.href : undefined))
  } catch {
    return false
  }
  if (u.username || u.password) return false
  return u.origin === origin && !isPublicStreamPath(u.pathname)
}

/** The token in hand, for the tests and the UI. */
export function streamTokenHeld(): StreamToken | null {
  return held
}

/** The last reason the site or the host refused, if any. */
export function lastStreamDenial(): StreamDenial | null {
  return lastDenial
}

/** Told when the host (or the site) refuses a stream: the app shows a notice. Returns an unsubscribe. */
export function onStreamDenied(listener: (why: StreamDenial) => void): () => void {
  deniedListeners.add(listener)
  return () => deniedListeners.delete(listener)
}

/** Tell the listeners, at most once a minute (a page can refuse many segments at once). */
function tell(why: StreamDenial, now = Date.now()): void {
  if (now - lastToldAt < UNAUTHORIZED_REFETCH_MS) return
  lastToldAt = now
  for (const listener of deniedListeners) listener(why)
}

function clearTimer(): void {
  if (renewTimer) clearTimeout(renewTimer)
  renewTimer = null
}

/** Forget the token (sign-out, leaving a meeting): the next stream request asks afresh. */
export function clearStreamToken(): void {
  held = null
  askAgainAt = 0
  lastDenial = null
  clearTimer()
}

/**
 * The meeting this browser sits in, with its seat's ticket (from the
 * room's welcome): the token then carries what the host lends. Null when
 * leaving. A change drops the token held, since its packages were the old
 * situation's.
 */
export function setStreamMeeting(room: string | null, ticket: string | null): void {
  const next = room && ticket ? { room, ticket } : null
  if ((next?.room ?? null) === (meeting?.room ?? null) && (next?.ticket ?? null) === (meeting?.ticket ?? null)) return
  meeting = next
  clearStreamToken()
  if (meeting) void ensureStreamToken()
}

/** The Studio render job in the address (`?render=1&job=<id>&t=<job token>`), or null. */
export function renderJobFromSearch(search: string): { job: string; t: string } | null {
  const params = new URLSearchParams(search)
  const render = params.get('render')
  const job = params.get('job') ?? ''
  const t = params.get('t') ?? ''
  if (render === null || render === '0' || render.toLowerCase() === 'false') return null
  return /^[a-f0-9]{16}$/.test(job) && t.length > 0 && t.length <= 200 ? { job, t } : null
}

/**
 * A Studio render's job (the GPU worker's headless browser is signed in to
 * nothing): the token is then asked for with the job's token and carries the
 * ordering account's packages (security, 2026-11-01: enforce without breaking
 * renders). Null clears it.
 */
export function setStreamRenderJob(next: { job: string; t: string } | null): void {
  if ((next?.job ?? null) === (renderJob?.job ?? null) && (next?.t ?? null) === (renderJob?.t ?? null)) return
  renderJob = next
  clearStreamToken()
  if (renderJob) void ensureStreamToken()
}

/** For the tests: the meeting the token is asked for. */
export function streamMeeting(): { room: string; ticket: string } | null {
  return meeting
}

function parseReply(body: unknown, now: number): StreamToken | null {
  if (typeof body !== 'object' || body === null) return null
  const b = body as Record<string, unknown>
  if (typeof b.token !== 'string' || !/^v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(b.token)) return null
  if (typeof b.expiresAt !== 'number' || typeof b.base !== 'string') return null
  try {
    const base = new URL(b.base)
    if (base.protocol !== 'https:' && !(base.protocol === 'http:' && (base.hostname === 'localhost' || base.hostname === '127.0.0.1'))) return null
    if (base.username || base.password || base.search || base.hash) return null
  } catch {
    return null
  }
  const issuedAt = typeof b.issuedAt === 'number' ? b.issuedAt : now
  if (b.expiresAt <= now) return null
  return { token: b.token, issuedAt, expiresAt: b.expiresAt, base: b.base }
}

function scheduleRenewal(now: number): void {
  clearTimer()
  if (!held) return
  renewTimer = setTimeout(() => { void renew() }, renewalDelay(held.issuedAt, held.expiresAt, now))
}

/** A renewal: the old token stays until the new one arrives; a failure tries again soon while the old one lasts. */
async function renew(): Promise<void> {
  const before = held
  const token = await request(Date.now())
  if (token === null && before && held === before) {
    clearTimer()
    renewTimer = setTimeout(() => { void renew() }, RENEW_RETRY_MS)
  }
}

async function request(now: number): Promise<string | null> {
  const url = renderJob
    ? `${STREAM_TOKEN_URL}?job=${encodeURIComponent(renderJob.job)}&t=${encodeURIComponent(renderJob.t)}`
    : meeting
    ? `${STREAM_TOKEN_URL}?room=${encodeURIComponent(meeting.room)}&ticket=${encodeURIComponent(meeting.ticket)}`
    : STREAM_TOKEN_URL
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
    if (res.status === 200) {
      const parsed = parseReply(await res.json().catch(() => null), Date.now())
      if (parsed) {
        held = parsed
        lastDenial = null
        askAgainAt = 0
        scheduleRenewal(Date.now())
        return parsed.token
      }
      logger.warn('[streamToken] The site answered with something that is not a token')
    } else {
      lastDenial = res.status === 401 ? 'signed_out' : res.status === 403 ? 'no_streams' : lastDenial
      if (res.status !== 401 && res.status !== 403 && res.status !== 503) logger.warn(`[streamToken] ${STREAM_TOKEN_URL} answered ${res.status}`)
    }
  } catch (err) {
    logger.warn('[streamToken] The site could not be asked for a stream token', err)
  }
  askAgainAt = now + ASK_AGAIN_AFTER_MS
  return null
}

/**
 * The token to send now: the one held while it lasts, else a fresh one
 * from the site; null when there is none to be had (signed out and not in
 * a meeting, a plan with no streams, the site not set up for tokens, or
 * asked too recently after one of those). Concurrent callers share one
 * request.
 */
export function ensureStreamToken(now = Date.now()): Promise<string | null> {
  if (held && held.expiresAt > now) return Promise.resolve(held.token)
  if (inFlight) return inFlight
  if (now < askAgainAt) return Promise.resolve(null)
  if (accountState().status === 'signedOut' && !meeting && !renderJob) {
    lastDenial = 'signed_out'
    return Promise.resolve(null)
  }
  inFlight = request(now).finally(() => { inFlight = null })
  return inFlight
}

/**
 * The host refused a request (401): the token held is dropped and a new
 * one asked for, at most once a minute. True when a fetch was started.
 */
export function noteStreamUnauthorized(now = Date.now()): boolean {
  if (now - lastUnauthorizedAt < UNAUTHORIZED_REFETCH_MS) return false
  lastUnauthorizedAt = now
  held = null
  askAgainAt = 0
  clearTimer()
  void ensureStreamToken(now)
  return true
}

/** A 401 came back from the host for a stream: refetch (throttled) and tell the app why. */
function refused(hadToken: boolean, now = Date.now()): void {
  if (hadToken) noteStreamUnauthorized(now)
  const why: StreamDenial = hadToken ? 'unauthorized' : lastDenial ?? 'signed_out'
  tell(why, now)
}

function urlOf(input: RequestInfo | URL): string {
  return typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
}

function withAuth(init: RequestInit | undefined, header: string): RequestInit {
  const headers = new Headers(init?.headers)
  headers.set('Authorization', header)
  // No cookie to the host: the token is the whole credential.
  return { ...init, headers, credentials: 'omit' }
}

function fetchWith(input: RequestInfo | URL, init: RequestInit | undefined, header: string): Promise<Response> {
  if (input instanceof Request) return fetch(new Request(input, withAuth(init, header)))
  return fetch(input, withAuth(init, header))
}

/**
 * `fetch` with the right token: an own dataset's for its own scope
 * (`privateData.authHeaderFor`), the stream token for the stream host's
 * gated files, nothing for anything else. A 401 from the host is noted.
 */
export const streamAuthFetch: typeof fetch = async (input, init) => {
  const url = urlOf(input)
  const own = authHeaderFor(url)
  if (own) return fetchWith(input, init, own)
  if (!needsStreamToken(url)) return fetch(input, init)
  const token = await ensureStreamToken()
  const res = token ? await fetchWith(input, init, `Bearer ${token}`) : await fetch(input, init)
  if (res.status === 401) refused(token !== null)
  return res
}

/** dash.js' request interceptor: own data's scopes first (`dashAuthInterceptor`), then the stream token. */
export const streamDashInterceptor = async <R extends { url: string; headers?: Record<string, string>; credentials?: RequestCredentials }>(request: R): Promise<R> => {
  const r = await dashAuthInterceptor(request)
  if (r.headers?.Authorization || !needsStreamToken(r.url)) return r
  const token = await ensureStreamToken()
  if (token) {
    r.headers = { ...(r.headers ?? {}), Authorization: `Bearer ${token}` }
    r.credentials = 'omit'
  }
  return r
}

/** dash.js' response interceptor: a 401 from the host on a segment or manifest is noted like a fetch's. */
export const streamDashResponseInterceptor = <R extends { status?: number; url?: string; request?: { url?: string; headers?: Record<string, string> } }>(response: R): Promise<R> => {
  const url = response.url ?? response.request?.url ?? ''
  if (response.status === 401 && needsStreamToken(url)) refused(!!response.request?.headers?.Authorization)
  return Promise.resolve(response)
}

/**
 * Keep the token in step with the account: fetched once the site has said
 * who is signed in (or a meeting was joined), dropped on sign-out. Called
 * once by main.ts; returns an unsubscribe.
 */
export function startStreamToken(): () => void {
  return onAccountChange((state) => {
    if (state.status === 'unknown') return
    clearStreamToken()
    if (state.status === 'signedIn' || meeting || renderJob) void ensureStreamToken()
  })
}

/** For the tests: everything forgotten. */
export function resetStreamTokenForTests(): void {
  clearStreamToken()
  inFlight = null
  lastUnauthorizedAt = 0
  lastToldAt = 0
  meeting = null
  renderJob = null
  deniedListeners.clear()
}
